import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { StyledModalBody } from "../../../../../../components/Modals";
import { ButtonBodyVariant } from "../../../../../../components/ui/ButtonVariant";
import { useTranslation } from "react-i18next";
import { closeActiveModal, openModal } from "../../../../../../helper/modals";
import { Flex } from "../../../../../../components/ui/Flex";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../../../../components/ui/Form";
import { useForm } from "react-hook-form";
import { ModalButtonsContainer, PaymentModal, StepItem, LightBlueText, TitleText, CurrencyInputContainer } from "../../../../PaymentComponents";
import * as Yup from 'yup';
import { yupResolver } from "@hookform/resolvers/yup";
import { updatePaymentData } from "../../../../../../redux/actions/payments";
import { Select, SelectItem } from "../../../../../../components/ui/Select";
import { TextInput } from "../../../../../../components/ui/TextInput";
import { Palette } from "../../../../../../components/utils";
import { createBuildingPaymentPurposeAndRefreshCache, ensureRentPaymentPurposesCached } from "../../../../PaymentUtils";

/** Sentinel value for the "Add new" row in the purpose Select (not a real payment_purposes.id). */
const ADD_NEW_PURPOSE_VALUE = '__add_new__';

const MAX_PURPOSE_DESCRIPTOR_LENGTH = 7;

const DEFAULT_PURPOSE_LABEL = 'Rent Adjustment';

function defaultPurposeIdFromOptions(options) {
  if (!options?.length) return '';
  const match = options.find(
    (p) => String(p.label || '').trim().toLowerCase() === DEFAULT_PURPOSE_LABEL.toLowerCase()
  );
  return String((match ?? options[0]).id);
}

const schema = Yup.object().shape({
  amount: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .typeError('Invalid Amount')
    .required('An amount is required')
    .max(10000, 'Amount exceeds the limit $10,000'),
  paymentPurposeId: Yup.string()
    .required('Select or add a purpose')
    .notOneOf([ADD_NEW_PURPOSE_VALUE], 'Enter a name and click Add, or choose another purpose'),
  notes: Yup.string().optional()
});

const CreateOneTimePaymentStep1 = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [purposeOptions, setPurposeOptions] = useState([]);
  const [newPurposeText, setNewPurposeText] = useState('');
  const [newPurposeDescriptor, setNewPurposeDescriptor] = useState('');
  const [addingPurpose, setAddingPurpose] = useState(false);
  const [addPurposeError, setAddPurposeError] = useState('');
  const [addPurposeDescriptorError, setAddPurposeDescriptorError] = useState('');

  const modal = useSelector((state) => state.headerModal.modal);
  const addPayment = useSelector((state) => state.addPayment);

  const form = useForm({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      amount: addPayment.amount,
      paymentPurposeId: addPayment.paymentPurposeId ? String(addPayment.paymentPurposeId) : '',
      notes: addPayment.notes
    }
  });

  const buildingId = addPayment.selectedBuilding?.id;

  useEffect(() => {
    let active = true;
    if (!buildingId) {
      setPurposeOptions([]);
      return () => {
        active = false;
      };
    }
    ensureRentPaymentPurposesCached(buildingId).then((data) => {
      if (!active) return;
      setPurposeOptions(Array.isArray(data) ? data : []);
    });
    return () => {
      active = false;
    };
  }, [buildingId]);

  useEffect(() => {
    const current = form.getValues('paymentPurposeId');
    if (current === ADD_NEW_PURPOSE_VALUE) return;

    const exists = purposeOptions.some((p) => String(p.id) === String(current));
    if (exists) return;

    if (purposeOptions.length) {
      form.setValue('paymentPurposeId', defaultPurposeIdFromOptions(purposeOptions), { shouldValidate: true });
    }
  }, [purposeOptions, form, buildingId]);

  const handleAddCustomPurpose = async () => {
    const text = newPurposeText.trim();
    const descriptor = newPurposeDescriptor.trim();
    if (!text || !buildingId) return;
    if (!descriptor) {
      setAddPurposeDescriptorError(t('Descriptor is required'));
      return;
    }
    if (descriptor.length > MAX_PURPOSE_DESCRIPTOR_LENGTH) {
      setAddPurposeDescriptorError(
        t('Descriptor must be at most {{max}} characters', { max: MAX_PURPOSE_DESCRIPTOR_LENGTH })
      );
      return;
    }
    setAddingPurpose(true);
    setAddPurposeError('');
    setAddPurposeDescriptorError('');
    const prevIds = new Set(purposeOptions.map((p) => p.id));
    const { list, error } = await createBuildingPaymentPurposeAndRefreshCache(buildingId, text, descriptor);
    setAddingPurpose(false);
    if (error || !list) {
      setAddPurposeError(error || 'Failed to add purpose');
      return;
    }
    setPurposeOptions(list);
    const candidates = list.filter((p) => !prevIds.has(p.id));
    const pick =
      candidates.find((p) => p.label === text && p.descriptor === descriptor) ||
      candidates.find((p) => p.label === text) ||
      candidates.reduce((best, p) => (!best || p.id > best.id ? p : best), null) ||
      list.find((p) => p.label === text) ||
      list[list.length - 1];
    if (pick) {
      form.setValue('paymentPurposeId', String(pick.id), { shouldValidate: true });
    }
    setNewPurposeText('');
    setNewPurposeDescriptor('');
  };

  const onSubmit = (values) => {
    const selectedPurpose = purposeOptions.find((item) => String(item.id) === values.paymentPurposeId);
    dispatch(updatePaymentData({
      ...values,
      paymentPurposeId: values.paymentPurposeId,
      paymentPurposeLabel: selectedPurpose?.label || '',
      paymentPurposeDescriptor: selectedPurpose?.descriptor || ''
    }))
    openModal('CreateOneTimePaymentStep2')
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <PaymentModal
          show={modal === 'CreateOneTimePaymentStep1'}
          onClose={closeActiveModal}
          closeOnBackdrop={false}
          width={'460px'}>
          <StyledModalBody>
            <Flex direction="column" gap="1rem">
              <Flex direction="column">
                <Flex justify="space-between" alignItems="center" style={{ width: "100%" }}>
                  <LightBlueText> STEP 1/2</LightBlueText>
                  <Flex gap="0.5rem">
                    <StepItem filled />
                    <StepItem />
                  </Flex>
                </Flex>
                <TitleText>Select Payment Amount</TitleText>
              </Flex>

              <Flex direction="column" alignItems="center" gap="1rem">

                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem alt>
                      <FormLabel v3>{t('Amount')}</FormLabel>
                      <FormControl>
                        <CurrencyInputContainer>
                          <TextInput
                            type="text"
                            placeholder={t('Amount')}
                            autoComplete="off"
                            {...field}
                          />
                        </CurrencyInputContainer>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="paymentPurposeId"
                  render={({ field }) => {
                    const showAddNewRow = field.value === ADD_NEW_PURPOSE_VALUE;
                    return (
                    <FormItem alt>
                      <FormLabel v3>{t('Purpose')}</FormLabel>
                      <Flex direction="column" gap="0.5rem" style={{ width: '100%' }}>
                        <FormControl row>
                          <Select
                            getPopupContainer={() => document.body}
                            height="3rem"
                            disabled={!buildingId}
                            value={field.value}
                            onChange={(v) => {
                              field.onChange(v);
                              if (v !== ADD_NEW_PURPOSE_VALUE) {
                                setNewPurposeText('');
                                setNewPurposeDescriptor('');
                                setAddPurposeError('');
                                setAddPurposeDescriptorError('');
                              }
                            }}
                            onBlur={field.onBlur}>
                            {purposeOptions.map((purpose) => (
                              <SelectItem key={purpose.id} value={String(purpose.id)}>
                                {purpose.label}
                              </SelectItem>
                            ))}
                            <SelectItem value={ADD_NEW_PURPOSE_VALUE}>{t('Add new')}</SelectItem>
                          </Select>
                        </FormControl>
                        {showAddNewRow ? (
                          <>
                            <CustomPurposePanel>
                              <TextInput
                                type="text"
                                placeholder={t('Enter custom purpose')}
                                autoComplete="off"
                                value={newPurposeText}
                                onChange={(e) => setNewPurposeText(e.target.value)}
                                disabled={!buildingId || addingPurpose}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddCustomPurpose();
                                  }
                                }}
                              />
                              <TextInput
                                type="text"
                                placeholder={t('Enter descriptor (max 7 characters)')}
                                autoComplete="off"
                                maxLength={MAX_PURPOSE_DESCRIPTOR_LENGTH}
                                value={newPurposeDescriptor}
                                onChange={(e) => {
                                  setNewPurposeDescriptor(e.target.value.slice(0, MAX_PURPOSE_DESCRIPTOR_LENGTH));
                                  setAddPurposeDescriptorError('');
                                }}
                                disabled={!buildingId || addingPurpose}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddCustomPurpose();
                                  }
                                }}
                              />
                              <ButtonBodyVariant
                                type="button"
                                variant="secondary"
                                cssOverride="width: 100%; height: 2rem;"
                                disabled={
                                  !buildingId ||
                                  addingPurpose ||
                                  !newPurposeText.trim() ||
                                  !newPurposeDescriptor.trim()
                                }
                                onClick={handleAddCustomPurpose}>
                                {t('Add')}
                              </ButtonBodyVariant>
                            </CustomPurposePanel>
                            {(addPurposeDescriptorError || addPurposeError) ? (
                              <span style={{ color: 'var(--destructive, #c00)', fontSize: '0.875rem' }}>
                                {addPurposeDescriptorError || addPurposeError}
                              </span>
                            ) : null}
                          </>
                        ) : null}
                      </Flex>
                      <FormMessage />
                    </FormItem>
                    );
                  }}
                />

                {/* <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem alt>
                      <FormLabel v3>{t('Enter Note')}</FormLabel>
                      <FormControl>
                        <SettingsModalInput
                          type="text"
                          placeholder={t('Notes')}
                          autoComplete="off"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                /> */}
              </Flex>

              <ModalButtonsContainer gapY="8px">
                <ButtonBodyVariant cssOverride="width: 8.875rem; height: 2rem;" type="button" variant="secondary" onClick={closeActiveModal}>{t('Cancel')}</ButtonBodyVariant>
                <ButtonBodyVariant cssOverride="width: 8.875rem; height: 2rem;" type="submit">{t('Next')}</ButtonBodyVariant>
              </ModalButtonsContainer>
            </Flex>
          </StyledModalBody>
        </PaymentModal>
      </form>
    </Form>
  )
}

const CustomPurposePanel = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 0.625rem;
  border: 1px solid ${Palette.DISABLED};
  background: ${Palette.WHITE};
`;

export default CreateOneTimePaymentStep1
